import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useGetPersonByIdQuery, useUpdatePersonSafeMutation } from "@/services/api";
import { transformSDateToString, transformStringToSDate } from "@/shared/utils";
import type { GeneralInfoTabFormValues } from "../types";
import { generalInfoTabSchema } from "../schema";

export const useGeneralInfoTab = (isEditMode: boolean) => {
  const { personId } = useParams();

  const { data: person, isFetching } = useGetPersonByIdQuery(
    { id: personId ?? "" },
    {
      skip: !personId || personId === "TEMP_ID",
    },
  );

  const updatePerson = useUpdatePersonSafeMutation();

  const form = useForm<GeneralInfoTabFormValues>({
    resolver: yupResolver(generalInfoTabSchema),
  });

  useEffect(() => {
    if (person && personId !== "TEMP_ID") {
      form.reset({
        websites: person.websites,
        phones: person.phones,
        emails: person.emails,
        socialNetworks: person.socialNetworks,
        addresses:
          person.addresses?.map((addr) => ({ ...addr, personId: addr.personId || undefined })) ??
          [],
        anniversaries:
          person.anniversaries.map((anniversary) => ({
            ...anniversary,
            anniversary: transformSDateToString(anniversary.anniversarySDate),
          })) ?? [],
      });
    } else {
      form.reset({
        websites: [],
        phones: [],
        emails: [],
        socialNetworks: [],
        addresses: [],
        anniversaries: [],
      });
    }
  }, [personId, isFetching, isEditMode]);

  const handleSubmit = (data: GeneralInfoTabFormValues) => {
    if (!person) return;

    updatePerson({
      ...person,
      phones:
        data.phones?.map((phone) => ({
          id: phone.id ?? undefined,
          personId: personId ?? "",
          number: phone.number ?? "",
          phoneType: { name: phone.phoneType?.name ?? "" },
        })) ?? [],
      emails:
        data.emails?.map((email) => ({
          id: email.id ?? undefined,
          personId: personId ?? "",
          email: email.email ?? "",
          emailType: { name: email.emailType?.name ?? "" },
        })) ?? [],
      websites:
        data.websites?.map((website) => ({
          id: website.id ?? undefined,
          personId: personId ?? "",
          websiteAddress: website.websiteAddress ?? "",
          websiteType: { name: website.websiteType?.name ?? "" },
        })) ?? [],
      socialNetworks:
        data.socialNetworks?.map((socialNetwork) => ({
          id: socialNetwork.id ?? undefined,
          personId: personId ?? "",
          socialNetwork: socialNetwork.socialNetwork ?? "",
          socialNetworkType: { name: socialNetwork.socialNetworkType?.name ?? "" },
        })) ?? [],
      addresses:
        data.addresses?.map((address) => ({
          id: address.id ?? undefined,
          personId: personId ?? "",
          addressType: { name: address.addressType?.name ?? "" },
          addressStreet1: address.addressStreet1 ?? "",
          addressStreet2: address.addressStreet2 ?? "",
          addressCity: address.addressCity ?? "",
          addressCountry: address.addressCountry ?? "",
          addressPostalCode: address.addressPostalCode ?? "",
        })) ?? [],
      anniversaries:
        data.anniversaries?.map((anniversary) => ({
          id: anniversary.id ?? undefined,
          personId: personId ?? "",
          anniversaryType: anniversary.anniversaryType ?? "",
          description: anniversary.description ?? "",
          anniversary: anniversary.anniversary ?? "",
          createdDate: new Date().toISOString(),
          anniversarySDate: transformStringToSDate(anniversary.anniversary ?? ""),
        })) ?? [],
    })
      .unwrap()
      .then((response) => {
        form.reset(
          {
            websites: response.websites,
            phones: response.phones,
            emails: response.emails,
            socialNetworks: response.socialNetworks,
            addresses:
              response.addresses?.map((addr) => ({
                ...addr,
                personId: addr.personId || undefined,
              })) ?? [],
            anniversaries:
              response.anniversaries.map((anniversary) => ({
                ...anniversary,
                anniversary: transformSDateToString(anniversary.anniversarySDate),
              })) ?? [],
          },
          {
            keepValues: true,
          },
        );
      });
  };

  return {
    state: {
      form,
      person,
      isLoading: isFetching,
      personId,
      addDisabled: personId === "TEMP_ID",
    },
    methods: { handleSubmit },
  };
};
